import { BusinessObjectBase } from './base';
import type { Role } from './role';
import type { Token } from './token';
import type { User } from './user';
import type { UserTaskHistory } from './user-task-history';

export interface UserTaskData {
  readonly id: string;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly status?: string;
  readonly data?: any;
  readonly completedAt?: Date;
  readonly tokenId: number;
  readonly token?: Token;
  readonly roleId: number;
  readonly role?: Role;
  readonly userId: number;
  readonly user?: User;
  readonly tokens?: Token[];
  readonly userTaskHistories?: UserTaskHistory[];
}

export interface UserTask extends Readonly<UserTaskData> {}
export class UserTask extends BusinessObjectBase {
  static readonly entityName = 'UserTask' as const;
  static readonly plural = 'user-tasks';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<UserTask[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new UserTask(data));
  }

  static async findOne(filter?: any): Promise<UserTask | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new UserTask(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new UserTask(data);
  }

  static async create(data: Omit<UserTaskData, 'id'>): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new UserTask(result);
  }

  static async updateById(
    id: string,
    data: Partial<UserTaskData>,
  ): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new UserTask(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async updateData(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/update-data`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async unclaim(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/unclaim`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async reassign(userId: number): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/reassign`;
    return BusinessObjectBase.post<any>(url, { userId });
  }
  async complete(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/complete`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async claim(userId?: number): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/claim`;
    return BusinessObjectBase.post<any>(url, { userId });
  }
}
